import React, { useState, useEffect } from 'react';
import { Activity, ShieldAlert, Building2, UserPlus, CheckCircle2, Radio } from 'lucide-react';
import { timeAgo } from '../../utils/formatDate';
import { notificationApi } from '../../api/notificationApi';

const ActivityFeed = ({ socket }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await notificationApi.getNotifications({ limit: 15 });
      const list = res?.data?.notifications || res?.notifications || [];
      const mapped = list.map(item => {
        let Icon = CheckCircle2;
        let color = 'text-emerald-600 bg-emerald-50 border-emerald-200/60';
        
        if (item.type === 'limit_request') {
          Icon = ShieldAlert;
          color = 'text-amber-600 bg-amber-50 border-amber-200/60';
        } else if (item.type === 'company_onboarded' || item.type === 'system') {
          Icon = Building2;
          color = 'text-teal-600 bg-teal-50 border-teal-200/60';
        } else if (item.type === 'admin_chat') {
          Icon = Radio;
          color = 'text-cyan-600 bg-cyan-50 border-cyan-200/60';
        }

        return {
          id: item._id,
          type: item.type,
          icon: Icon,
          color: color,
          title: item.title || 'System Alert',
          message: item.message,
          timestamp: item.createdAt
        };
      });
      setActivities(mapped);
    } catch (err) {
      console.error('Failed to fetch notifications for feed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    if (socket) {
      const handleNewNotification = () => {
        fetchNotifications();
      };

      socket.on('notification_received', handleNewNotification);
      socket.on('chat_reply_received', handleNewNotification);
      return () => {
        socket.off('notification_received', handleNewNotification);
        socket.off('chat_reply_received', handleNewNotification);
      };
    }
  }, [socket]);

  return (
    <div className="p-6 rounded-2xl bg-white/90 backdrop-blur-xl border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03)] flex flex-col h-full">
      {/* Stream Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white shadow-glow-emerald">
            <Activity className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-slate-900 font-display tracking-tight">Live System Stream</h3>
            <p className="text-[10px] text-slate-400 font-medium">Real-time event & socket telemetry</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/60 text-[10px] font-extrabold text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
          <span>STREAM ACTIVE</span>
        </div>
      </div>

      {/* Activity List */}
      <div className="space-y-3 overflow-y-auto flex-1 pr-1 custom-scrollbar">
        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center text-xs text-slate-400 font-medium gap-2">
            <span className="w-4 h-4 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
            Loading stream telemetry...
          </div>
        ) : activities.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400 font-medium">No recent system events reported.</div>
        ) : (
          activities.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="group flex items-start space-x-3.5 p-3.5 rounded-2xl bg-slate-50/60 hover:bg-white border border-slate-200/60 hover:border-emerald-200/80 hover:shadow-md transition-all duration-200"
              >
                <div className={`p-2.5 rounded-xl border flex-shrink-0 ${item.color} group-hover:scale-105 transition-transform`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-xs font-bold text-slate-900 truncate font-display group-hover:text-emerald-700 transition-colors">
                      {item.title}
                    </h4>
                    <span className="text-[10px] font-semibold text-slate-400 flex-shrink-0">
                      {timeAgo(item.timestamp)}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                    {item.message}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;

